import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, 
  ArrowRight, 
  TrendingUp, 
  Users, 
  Bot,
  Sparkles,
  Shield,
  Zap,
  BarChart3,
  Star,
  Clock,
  AlertTriangle,
  Target,
  DollarSign,
  Award,
  ThumbsUp,
  ChevronDown,
  Play,
  Quote,
  Building2,
  Globe,
  Smartphone,
  Search,
  MessageSquare
} from 'lucide-react';

import { API_URL } from '../config';
const API = `${API_URL}/api`;

// Template Components - All using UPost brand colors (Navy #1e3a5f, Blue #2563eb, Slate #475569, White)
const DefaultTemplate = ({ affiliate, affiliateCode, analyzerUrl, ogImageUrl }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    {/* Hero Section */}
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-slate-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="text-center mb-8">
          <Badge className="bg-white/10 text-white/90 backdrop-blur-sm px-4 py-2 text-sm">
            <Sparkles className="h-4 w-4 mr-2 inline" />
            Recommended by {affiliate?.name || 'Our Partner'}
          </Badge>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Is AI Recommending
            <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Your Business?
            </span>
          </h1>
          <p className="text-xl sm:text-2xl text-slate-300 max-w-2xl mx-auto">
            60% of consumers now ask ChatGPT, Perplexity & Claude for business recommendations.
            <strong className="text-white"> Are you showing up?</strong>
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
            <CardContent className="p-6 text-center">
              <Bot className="h-8 w-8 mx-auto mb-3 text-blue-400" />
              <div className="text-3xl font-bold mb-1">60%</div>
              <div className="text-sm text-slate-300">Use AI for recommendations</div>
            </CardContent>
          </Card>
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
            <CardContent className="p-6 text-center">
              <TrendingUp className="h-8 w-8 mx-auto mb-3 text-blue-400" />
              <div className="text-3xl font-bold mb-1">73/100</div>
              <div className="text-sm text-slate-300">AI mentions achieved</div>
            </CardContent>
          </Card>
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
            <CardContent className="p-6 text-center">
              <BarChart3 className="h-8 w-8 mx-auto mb-3 text-blue-400" />
              <div className="text-3xl font-bold mb-1">$89K</div>
              <div className="text-sm text-slate-300">Revenue boost in 60 days</div>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mb-8">
          <a href={analyzerUrl}>
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-8 py-6 h-auto shadow-2xl shadow-blue-500/30 transform hover:scale-105 transition-all"
              data-testid="check-ai-visibility-btn"
            >
              Check Your AI Visibility FREE
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
          <p className="text-slate-400 text-sm mt-4">
            Takes 30 seconds • No credit card required
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 text-slate-300 text-sm">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-400" />
            <span>100% Free Analysis</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-blue-400" />
            <span>Instant Results</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-400" />
            <span>500+ Businesses Analyzed</span>
          </div>
        </div>
      </div>
    </section>

    {/* Case Study */}
    <CaseStudySection analyzerUrl={analyzerUrl} />

    {/* What You'll Learn */}
    <WhatYouLearnSection />

    {/* Final CTA */}
    <FinalCTASection analyzerUrl={analyzerUrl} affiliateCode={affiliateCode} />
  </div>
);

const SalesFunnelTemplate = ({ affiliate, affiliateCode, analyzerUrl }) => (
  <div className="min-h-screen bg-slate-900">
    {/* Problem Section */}
    <section className="relative py-20 bg-gradient-to-b from-slate-800 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <Badge className="bg-slate-700 text-slate-200 mb-6">
          <AlertTriangle className="h-4 w-4 mr-2 inline" />
          Warning: Your Business May Be Invisible
        </Badge>
        
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
          While You're Reading This,
          <span className="block text-blue-400 mt-2">AI Is Sending Customers to Your Competitors</span>
        </h1>
        
        <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
          Every day, millions of potential customers ask ChatGPT and other AI assistants: 
          <em className="text-white">"Who's the best {'{your service}'} near me?"</em>
        </p>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-xl mx-auto mb-8">
          <p className="text-slate-300 text-lg">
            <strong className="text-blue-400">The harsh truth:</strong> If AI doesn't know your business exists, 
            you're losing customers you'll never know about.
          </p>
        </div>
      </div>
    </section>

    {/* Agitation Section */}
    <section className="py-16 bg-slate-900">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-white text-center mb-12">
          The Problem Is Getting Worse...
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { stat: "60%", label: "of consumers now use AI for recommendations", icon: <Bot className="h-6 w-6" /> },
            { stat: "73%", label: "of AI users trust AI recommendations over Google", icon: <ThumbsUp className="h-6 w-6" /> },
            { stat: "2026", label: "AI will influence $4.5T in purchases", icon: <DollarSign className="h-6 w-6" /> }
          ].map((item, i) => (
            <Card key={i} className="bg-slate-800 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="text-blue-400 mb-3 flex justify-center">{item.icon}</div>
                <div className="text-4xl font-bold text-white mb-2">{item.stat}</div>
                <p className="text-slate-400 text-sm">{item.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* Solution Section */}
    <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-800">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <Badge className="bg-blue-600/20 text-blue-300 mb-6">
          <CheckCircle2 className="h-4 w-4 mr-2 inline" />
          The Solution
        </Badge>
        
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
          Discover Your AI Visibility Score in 30 Seconds
        </h2>
        
        <p className="text-xl text-slate-300 mb-8">
          Our free analysis shows you exactly how AI sees your business — 
          and what you can do to get recommended.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          {[
            { step: "1", title: "Enter Your Website", desc: "Takes 10 seconds" },
            { step: "2", title: "We Query AI", desc: "ChatGPT, Perplexity, Claude" },
            { step: "3", title: "Get Your Score", desc: "Plus actionable tips" }
          ].map((item, i) => (
            <div key={i} className="bg-slate-800/50 rounded-xl p-6">
              <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mx-auto mb-3">
                {item.step}
              </div>
              <h3 className="text-white font-semibold mb-1">{item.title}</h3>
              <p className="text-slate-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        <a href={analyzerUrl}>
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-10 py-6 h-auto shadow-2xl shadow-blue-500/30"
          >
            Get My Free AI Visibility Score
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </a>
        
        <p className="text-slate-500 text-sm mt-4">
          Recommended by {affiliate?.name || 'our partner'} • No credit card required
        </p>
      </div>
    </section>

    {/* Social Proof */}
    <section className="py-16 bg-slate-900">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-white">Real Results from Real Businesses</h2>
        </div>
        
        <Card className="bg-gradient-to-br from-blue-900/30 to-slate-800 border-blue-500/30">
          <CardContent className="p-8">
            <Quote className="h-8 w-8 text-blue-400 mb-4" />
            <p className="text-xl text-white mb-6">
              "We went from 0 AI mentions to 73 out of 100 queries. That translated to 
              <span className="text-blue-400 font-bold"> $89,000 in new revenue</span> in just 60 days."
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Building2 className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-semibold">Texas RV Dealership</p>
                <p className="text-slate-400 text-sm">Family-owned business since 1985</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>

    <FinalCTASection analyzerUrl={analyzerUrl} affiliateCode={affiliateCode} dark />
  </div>
);

const CaseStudyTemplate = ({ affiliate, affiliateCode, analyzerUrl }) => (
  <div className="min-h-screen bg-white">
    {/* Hero */}
    <section className="bg-gradient-to-br from-slate-800 to-slate-900 text-white py-20">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <Badge className="bg-white/20 text-white mb-6">
          <Award className="h-4 w-4 mr-2 inline" />
          Case Study: Real Results
        </Badge>
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-6">
          How a Local Business Increased Revenue by $89,000
          <span className="block text-blue-200 text-2xl mt-4">By Getting Recommended by AI Assistants</span>
        </h1>
        
        <p className="text-xl text-blue-100 mb-8">
          Shared by {affiliate?.name || 'our partner'}
        </p>
      </div>
    </section>

    {/* The Challenge */}
    <section className="py-16 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
          <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
          The Challenge
        </h2>
        
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <p className="text-slate-700 text-lg">
              A family-owned RV dealership in Texas was struggling. Despite 30+ years in business and 
              hundreds of 5-star reviews, they were <strong>completely invisible to AI assistants</strong>.
            </p>
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <div className="text-3xl font-bold text-red-600">0</div>
                <div className="text-sm text-slate-600">AI Mentions</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <div className="text-3xl font-bold text-red-600">-15%</div>
                <div className="text-sm text-slate-600">YoY Traffic</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <div className="text-3xl font-bold text-red-600">3</div>
                <div className="text-sm text-slate-600">Competitors Ranked</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>

    {/* The Solution */}
    <section className="py-16">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
          <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
          The Solution
        </h2>
        
        <Card>
          <CardContent className="p-6">
            <p className="text-slate-700 text-lg mb-6">
              They used our <strong>free AI Visibility Analysis</strong> to understand exactly why 
              AI wasn't recommending them — and what to fix.
            </p>
            
            <div className="space-y-4">
              {[
                "Discovered their website lacked AI-readable structured data",
                "Found that competitors were optimizing for AI citations",
                "Identified 7 quick wins to improve AI visibility",
                "Implemented changes with our guided recommendations"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>

    {/* The Results */}
    <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
          <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
          The Results (After 60 Days)
        </h2>
        
        <div className="grid sm:grid-cols-3 gap-6 mb-8">
          <Card className="border-green-200 bg-white">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">73/100</div>
              <div className="text-slate-600">AI Mentions</div>
              <div className="text-xs text-green-600 mt-1">↑ from 0</div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-white">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">$89K</div>
              <div className="text-slate-600">New Revenue</div>
              <div className="text-xs text-green-600 mt-1">AI-attributed</div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-white">
            <CardContent className="p-6 text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">47%</div>
              <div className="text-slate-600">More Leads</div>
              <div className="text-xs text-green-600 mt-1">Month over month</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">
          Get the Same Analysis — Free
        </h2>
        <p className="text-blue-100 mb-8">
          Discover your AI visibility score and get personalized recommendations
        </p>
        
        <a href={analyzerUrl}>
          <Button 
            size="lg" 
            className="bg-white text-blue-600 hover:bg-blue-50 font-bold text-lg px-10 py-6 h-auto"
          >
            Start My Free Analysis
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </a>
        
        <p className="text-blue-200 text-sm mt-4">
          Partner: {affiliateCode} • No credit card required
        </p>
      </div>
    </section>
  </div>
);

const ComparisonTemplate = ({ affiliate, affiliateCode, analyzerUrl }) => (
  <div className="min-h-screen bg-slate-900">
    {/* Hero */}
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-slate-800/50 via-transparent to-blue-900/30"></div>
      
      <div className="max-w-5xl mx-auto px-4 relative">
        <div className="text-center mb-12">
          <Badge className="bg-white/10 text-white mb-6">
            <Sparkles className="h-4 w-4 mr-2 inline" />
            Recommended by {affiliate?.name || 'Our Partner'}
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Before & After: AI Visibility Transformation
          </h1>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Before */}
          <Card className="bg-slate-800 border-slate-600">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <Badge className="bg-slate-700 text-slate-300">BEFORE</Badge>
                <h3 className="text-2xl font-bold text-white mt-4">Invisible to AI</h3>
              </div>
              
              <div className="space-y-4">
                {[
                  { label: "AI Recommendations", value: "0 out of 100", bad: true },
                  { label: "Competitor Mentions", value: "Ranked 1-3", bad: true },
                  { label: "AI Traffic", value: "None tracked", bad: true },
                  { label: "Lost Opportunities", value: "Unknown", bad: true }
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                    <span className="text-slate-300">{item.label}</span>
                    <span className="text-slate-400 font-semibold">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* After */}
          <Card className="bg-blue-900/30 border-blue-500/30">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <Badge className="bg-blue-600/30 text-blue-300">AFTER 60 DAYS</Badge>
                <h3 className="text-2xl font-bold text-white mt-4">AI Recommends You</h3>
              </div>
              
              <div className="space-y-4">
                {[
                  { label: "AI Recommendations", value: "73 out of 100" },
                  { label: "Your Ranking", value: "#1 in category" },
                  { label: "AI Traffic", value: "+340% increase" },
                  { label: "New Revenue", value: "$89,000" }
                ].map((item, i) => (
                  <div key={i} className="flex justify-between items-center p-3 bg-blue-900/30 rounded-lg">
                    <span className="text-blue-200">{item.label}</span>
                    <span className="text-blue-400 font-semibold">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-12">
          <a href={analyzerUrl}>
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-10 py-6 h-auto"
            >
              See Where You Stand — Free Analysis
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </div>
    </section>

    {/* How It Works */}
    <section className="py-16 bg-slate-800">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-white text-center mb-10">
          How We Transform Your AI Visibility
        </h2>
        
        <div className="grid sm:grid-cols-4 gap-4">
          {[
            { icon: <Search className="h-6 w-6" />, title: "Analyze", desc: "We scan all major AI platforms" },
            { icon: <Target className="h-6 w-6" />, title: "Identify", desc: "Find gaps & opportunities" },
            { icon: <Zap className="h-6 w-6" />, title: "Optimize", desc: "Implement winning changes" },
            { icon: <TrendingUp className="h-6 w-6" />, title: "Dominate", desc: "Get recommended by AI" }
          ].map((item, i) => (
            <Card key={i} className="bg-slate-700 border-slate-600">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-400">
                  {item.icon}
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    <FinalCTASection analyzerUrl={analyzerUrl} affiliateCode={affiliateCode} dark />
  </div>
);

const UrgencyTemplate = ({ affiliate, affiliateCode, analyzerUrl }) => (
  <div className="min-h-screen bg-slate-900">
    {/* Urgency Banner */}
    <div className="bg-blue-600 py-3 text-center">
      <p className="text-white font-semibold flex items-center justify-center gap-2">
        <Clock className="h-4 w-4 animate-pulse" />
        Limited Time: Free AI Visibility Analysis (Usually $497)
        <Clock className="h-4 w-4 animate-pulse" />
      </p>
    </div>

    {/* Hero */}
    <section className="py-20 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-slate-900"></div>
      
      <div className="max-w-4xl mx-auto px-4 relative text-center">
        <Badge className="bg-blue-600/20 text-blue-300 mb-6 animate-pulse">
          <AlertTriangle className="h-4 w-4 mr-2 inline" />
          AI is Changing Everything — Are You Ready?
        </Badge>
        
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
          Your Competitors Are Already
          <span className="block text-blue-400">Getting Recommended by AI</span>
        </h1>
        
        <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
          Every day you wait, AI assistants are sending YOUR potential customers 
          to businesses that optimized first.
        </p>

        {/* Countdown-style Stats */}
        <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto mb-12">
          <div className="bg-slate-800 border border-blue-500/30 rounded-xl p-4">
            <div className="text-4xl font-bold text-blue-400">60%</div>
            <div className="text-xs text-slate-400">Using AI Now</div>
          </div>
          <div className="bg-slate-800 border border-blue-500/30 rounded-xl p-4">
            <div className="text-4xl font-bold text-blue-400">$4.5T</div>
            <div className="text-xs text-slate-400">AI-Influenced Sales</div>
          </div>
          <div className="bg-slate-800 border border-blue-500/30 rounded-xl p-4">
            <div className="text-4xl font-bold text-blue-400">2026</div>
            <div className="text-xs text-slate-400">The AI Tipping Point</div>
          </div>
        </div>

        <a href={analyzerUrl}>
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xl px-12 py-8 h-auto shadow-2xl shadow-blue-500/30"
          >
            Claim Your Free Analysis Now
            <ArrowRight className="ml-3 h-6 w-6" />
          </Button>
        </a>
        
        <p className="text-slate-500 text-sm mt-4">
          Referred by {affiliate?.name || 'our partner'} • Only 50 free analyses remaining this month
        </p>
      </div>
    </section>

    {/* What You're Missing */}
    <section className="py-16 bg-slate-800">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-white text-center mb-10">
          What's At Stake If You Don't Act
        </h2>
        
        <div className="grid sm:grid-cols-2 gap-6">
          {[
            { icon: <Users className="h-6 w-6" />, title: "Lost Customers", desc: "AI sends them to competitors who show up" },
            { icon: <DollarSign className="h-6 w-6" />, title: "Lost Revenue", desc: "Thousands in sales you'll never know about" },
            { icon: <TrendingUp className="h-6 w-6" />, title: "Falling Behind", desc: "The gap widens every day you wait" },
            { icon: <Clock className="h-6 w-6" />, title: "First-Mover Advantage", desc: "Early optimizers dominate their markets" }
          ].map((item, i) => (
            <Card key={i} className="bg-slate-900 border-slate-700">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-400 shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* Final Push */}
    <section className="py-20 bg-gradient-to-t from-slate-800 to-slate-900">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">
          Don't Let Another Day Pass
        </h2>
        <p className="text-slate-300 mb-8">
          Find out in 30 seconds if AI knows your business exists
        </p>
        
        <a href={analyzerUrl}>
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-10 py-6 h-auto"
          >
            Get My Free Score Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </a>
        
        <div className="mt-8 flex items-center justify-center gap-4 text-slate-400 text-sm">
          <div className="flex items-center gap-1">
            <Shield className="h-4 w-4 text-blue-400" />
            <span>No credit card</span>
          </div>
          <div className="flex items-center gap-1">
            <Zap className="h-4 w-4 text-yellow-400" />
            <span>Instant results</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle2 className="h-4 w-4 text-blue-400" />
            <span>100% free</span>
          </div>
        </div>
      </div>
    </section>

    {/* Footer */}
    <footer className="py-6 border-t border-slate-800 text-center">
      <p className="text-slate-500 text-sm">
        © 2026 UPost • Partner: {affiliateCode}
      </p>
    </footer>
  </div>
);

// Shared Components
const CaseStudySection = ({ analyzerUrl }) => (
  <section className="bg-white/5 backdrop-blur-sm py-16">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <Badge className="bg-blue-600/20 text-blue-300 mb-4">Case Study</Badge>
        <h2 className="text-2xl sm:text-3xl font-bold text-white">
          Real Results from Real Businesses
        </h2>
      </div>

      <Card className="bg-white/10 backdrop-blur-sm border-white/20">
        <CardContent className="p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-blue-500/20 rounded-full p-3">
              <Star className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Texas RV Dealer Success Story</h3>
              <p className="text-slate-300">
                A family-owned RV dealership was invisible to AI assistants. After optimization:
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <div className="text-sm text-slate-400 mb-1">Before</div>
              <div className="text-2xl font-bold text-slate-400">0 mentions</div>
              <div className="text-xs text-slate-500">out of 100 AI queries</div>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <div className="text-sm text-slate-400 mb-1">After 60 Days</div>
              <div className="text-2xl font-bold text-blue-400">73 mentions</div>
              <div className="text-xs text-slate-500">out of 100 AI queries</div>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <div className="text-sm text-slate-400 mb-1">Revenue Impact</div>
              <div className="text-2xl font-bold text-blue-400">+$89,000</div>
              <div className="text-xs text-slate-500">attributed to AI traffic</div>
            </div>
          </div>

          <div className="text-center">
            <a href={analyzerUrl}>
              <Button className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">
                Get Your Free Analysis
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  </section>
);

const WhatYouLearnSection = () => (
  <section className="py-16">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
          What You'll Discover
        </h2>
        <p className="text-slate-300">
          Our free analysis reveals exactly how AI sees your business
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {[
          { icon: <Bot className="h-6 w-6" />, title: "AI Visibility Score", description: "See how often your business appears in ChatGPT, Perplexity, and Claude responses" },
          { icon: <BarChart3 className="h-6 w-6" />, title: "Competitor Comparison", description: "Learn which competitors AI recommends instead of you" },
          { icon: <Zap className="h-6 w-6" />, title: "Quick Wins", description: "Get actionable tips to improve your AI visibility immediately" },
          { icon: <TrendingUp className="h-6 w-6" />, title: "Growth Opportunities", description: "Discover untapped AI-driven traffic sources for your industry" }
        ].map((item, idx) => (
          <Card key={idx} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="bg-blue-500/30 rounded-lg p-3 text-blue-300">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-300 text-sm">{item.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

const FinalCTASection = ({ analyzerUrl, affiliateCode, dark = false }) => (
  <>
    <section className={`py-16 ${dark ? 'bg-gradient-to-t from-slate-950/50' : 'bg-gradient-to-t from-slate-800/30'}`}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
          Don't Let AI Recommend Your Competitors
        </h2>
        <p className="text-slate-300 mb-8">
          Find out in 30 seconds if ChatGPT, Claude & Gemini recommend your business.
        </p>
        <a href={analyzerUrl}>
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-10 py-6 h-auto shadow-2xl shadow-blue-500/30"
          >
            Check If AI Recommends Me
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </a>
        
        <div className="mt-8 flex items-center justify-center gap-2 text-slate-400 text-sm">
          <CheckCircle2 className="h-4 w-4 text-blue-400" />
          <span>Real AI queries • Not simulated • Instant results</span>
        </div>
      </div>
    </section>

    <footer className="py-8 border-t border-white/10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-slate-400 text-sm">
          <div>© 2026 UPost. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
          </div>
          <div className="text-slate-500">Partner: {affiliateCode}</div>
        </div>
      </div>
    </footer>
  </>
);

// ==================== INDUSTRY-SPECIFIC TEMPLATES ====================

// Factory function to create industry templates with customized content
const createIndustryTemplate = (config) => {
  return ({ affiliate, affiliateCode, analyzerUrl }) => (
    <div className={`min-h-screen bg-gradient-to-br ${config.gradient}`}>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute -top-40 -right-40 w-80 h-80 ${config.accentBg} rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse`}></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-8">
            <Badge className="bg-white/10 text-white/90 backdrop-blur-sm px-4 py-2 text-sm mb-6">
              <config.icon className="h-4 w-4 mr-2 inline" />
              {config.badge}
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {config.headline}
            </h1>
            
            <p className="text-xl text-white/80 mb-4 max-w-2xl mx-auto">
              {config.subheadline}
            </p>
            
            <p className="text-lg text-amber-400 font-semibold mb-8">
              ⚠️ {config.urgency}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={analyzerUrl}>
                <Button size="lg" className={`${config.buttonClass} text-white font-semibold px-8 py-6 text-lg`}>
                  Check Your {config.businessType} Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mt-12">
            {config.stats.map((stat, idx) => (
              <div key={idx} className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-sm text-white/70">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
            {config.painPointHeadline}
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            {config.painPointSubheadline}
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {config.painPoints.map((point, idx) => (
              <Card key={idx} className="border-2 border-red-100 bg-red-50/50">
                <CardContent className="pt-6">
                  <div className="text-4xl mb-4">{point.emoji}</div>
                  <h3 className="font-bold text-slate-900 mb-2">{point.title}</h3>
                  <p className="text-sm text-slate-600">{point.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className={`py-16 px-4 ${config.solutionBg}`}>
        <div className="max-w-4xl mx-auto text-center">
          <Badge className={`${config.solutionBadge} mb-4`}>The Solution</Badge>
          <h2 className="text-3xl font-bold text-white mb-6">
            Get Found by AI When {config.searchQuery}
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            We optimize your online presence so ChatGPT, Google AI, and other AI assistants 
            recommend YOUR {config.businessType.toLowerCase()} to potential customers.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 text-left">
            {config.benefits.map((benefit, idx) => (
              <div key={idx} className="flex gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-white">{benefit.title}</h4>
                  <p className="text-sm text-white/70">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <a href={analyzerUrl} className="inline-block mt-8">
            <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 font-semibold px-8 py-6 text-lg">
              Free {config.businessType} AI Visibility Check
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Ready to Dominate AI Search?
          </h2>
          <p className="text-slate-600 mb-8">
            Join {config.socialProof} who've already optimized their {config.businessType.toLowerCase()} for AI visibility.
          </p>
          <a href={analyzerUrl}>
            <Button size="lg" className={`${config.buttonClass} text-white font-semibold px-12 py-6 text-xl`}>
              Get Started Free
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </a>
          <p className="text-sm text-slate-500 mt-4">
            Free analysis • No credit card required • Results in 60 seconds
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-900 text-slate-400 text-sm">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
          </div>
          <div className="text-slate-500">Partner: {affiliateCode}</div>
        </div>
      </footer>
    </div>
  );
};

// Industry Template Configurations
const AutoDealershipTemplate = createIndustryTemplate({
  gradient: 'from-slate-900 via-blue-900 to-slate-900',
  accentBg: 'bg-blue-600',
  icon: Building2,
  badge: 'For Auto Dealerships',
  headline: 'Is AI Recommending Your Dealership?',
  subheadline: 'When customers ask AI "best car dealership near me" or "where to buy a car in [city]" — is YOUR dealership being mentioned?',
  urgency: 'Your competitors are already optimizing for AI search. Don\'t get left behind.',
  buttonClass: 'bg-blue-600 hover:bg-blue-700',
  businessType: 'Dealership',
  searchQuery: 'Customers Search for Cars',
  stats: [
    { value: '73%', label: 'Car buyers use AI for research' },
    { value: '4.2x', label: 'More leads from AI visibility' },
    { value: '$847', label: 'Avg. cost per missed lead' }
  ],
  painPointHeadline: 'Your Dealership Is Invisible to AI',
  painPointSubheadline: 'While you focus on inventory, competitors are getting recommended by ChatGPT and Google AI.',
  painPoints: [
    { emoji: '🚗', title: '"Best dealership near me"', description: 'Customers ask AI this daily. Are you in the answer?' },
    { emoji: '📉', title: 'Declining Walk-ins', description: 'AI is the new search. Old SEO isn\'t enough anymore.' },
    { emoji: '💸', title: 'Wasted Ad Spend', description: 'Paying for ads while AI gives free recommendations to competitors.' }
  ],
  benefits: [
    { title: 'Inventory Visibility', description: 'Get your cars found in AI searches' },
    { title: 'Local Dominance', description: 'Be the #1 AI recommendation in your area' },
    { title: 'Review Integration', description: 'Leverage your reviews for AI trust signals' },
    { title: 'Competitive Edge', description: 'Beat competitors who aren\'t optimized yet' }
  ],
  solutionBg: 'bg-gradient-to-r from-blue-900 to-slate-900',
  solutionBadge: 'bg-blue-600 text-white',
  socialProof: '500+ dealerships'
});

const RestaurantTemplate = createIndustryTemplate({
  gradient: 'from-slate-900 via-blue-900 to-slate-900',
  accentBg: 'bg-blue-600',
  icon: Building2,
  badge: 'For Restaurants',
  headline: 'Is AI Recommending Your Restaurant?',
  subheadline: 'When hungry customers ask "best [cuisine] near me" or "where to eat tonight" — does AI mention YOUR restaurant?',
  urgency: 'Food trends spread through AI recommendations. Get in front of them.',
  buttonClass: 'bg-blue-600 hover:bg-blue-700',
  businessType: 'Restaurant',
  searchQuery: 'Diners Search for Food',
  stats: [
    { value: '68%', label: 'Diners consult AI first' },
    { value: '3.8x', label: 'More reservations' },
    { value: '$156', label: 'Avg. party spend' }
  ],
  painPointHeadline: 'Empty Tables While AI Sends Diners Elsewhere',
  painPointSubheadline: 'AI assistants are making restaurant recommendations right now. Are you on the list?',
  painPoints: [
    { emoji: '🍽️', title: '"Best restaurant near me"', description: 'AI answers this thousands of times daily in your city.' },
    { emoji: '📱', title: 'Changing Discovery', description: 'People ask AI before checking Yelp or Google Maps.' },
    { emoji: '🎯', title: 'Targeted Searches', description: '"Romantic dinner" "Family-friendly" — AI matches intent.' }
  ],
  benefits: [
    { title: 'Menu Visibility', description: 'Your signature dishes mentioned by AI' },
    { title: 'Ambiance Matching', description: 'Get recommended for the right occasions' },
    { title: 'Review Leverage', description: 'Your 5-star reviews boost AI rankings' },
    { title: 'Local Foodie Reach', description: 'Capture the AI-first dining crowd' }
  ],
  solutionBg: 'bg-gradient-to-r from-blue-900 to-slate-900',
  solutionBadge: 'bg-blue-600 text-white',
  socialProof: '1,200+ restaurants'
});

const DentalTemplate = createIndustryTemplate({
  gradient: 'from-slate-900 via-blue-900 to-slate-900',
  accentBg: 'bg-blue-600',
  icon: Building2,
  badge: 'For Dental Practices',
  headline: 'Is AI Recommending Your Practice?',
  subheadline: 'When patients ask "best dentist near me" or "emergency dental care" — is YOUR practice the AI answer?',
  urgency: 'Patients increasingly trust AI for healthcare recommendations.',
  buttonClass: 'bg-blue-600 hover:bg-blue-700',
  businessType: 'Practice',
  searchQuery: 'Patients Search for Dentists',
  stats: [
    { value: '61%', label: 'Patients use AI for dental search' },
    { value: '$2,400', label: 'Avg. patient lifetime value' },
    { value: '4.5x', label: 'More new patient inquiries' }
  ],
  painPointHeadline: 'New Patients Are Finding Dentists Through AI',
  painPointSubheadline: 'Your next patient is asking ChatGPT for a dentist recommendation right now.',
  painPoints: [
    { emoji: '🦷', title: '"Dentist accepting new patients"', description: 'AI answers this query constantly. Are you included?' },
    { emoji: '⚡', title: '"Emergency dentist near me"', description: 'High-value urgent care patients finding competitors.' },
    { emoji: '👨‍👩‍👧', title: '"Family dentist recommendations"', description: 'Families trust AI for healthcare provider selection.' }
  ],
  benefits: [
    { title: 'Service Visibility', description: 'All your treatments found by AI' },
    { title: 'Insurance Matching', description: 'Get recommended for specific insurance plans' },
    { title: 'Trust Building', description: 'Your credentials highlighted in AI responses' },
    { title: 'Emergency Capture', description: 'Be first for urgent dental needs' }
  ],
  solutionBg: 'bg-gradient-to-r from-blue-900 to-slate-900',
  solutionBadge: 'bg-blue-600 text-white',
  socialProof: '800+ dental practices'
});

const MedicalTemplate = createIndustryTemplate({
  gradient: 'from-slate-900 via-blue-900 to-slate-900',
  accentBg: 'bg-blue-600',
  icon: Building2,
  badge: 'For Medical Practices',
  headline: 'Is AI Recommending Your Practice?',
  subheadline: 'When patients search for doctors, AI assistants are making recommendations. Is your practice being mentioned?',
  urgency: 'Healthcare AI search is growing 300% year over year.',
  buttonClass: 'bg-blue-600 hover:bg-blue-700',
  businessType: 'Practice',
  searchQuery: 'Patients Search for Doctors',
  stats: [
    { value: '58%', label: 'Patients use AI for doctor search' },
    { value: '$4,200', label: 'Avg. patient lifetime value' },
    { value: '3.2x', label: 'More appointment requests' }
  ],
  painPointHeadline: 'Patients Are Choosing Doctors Based on AI',
  painPointSubheadline: 'Medical practice discovery is shifting to AI. Is your practice visible?',
  painPoints: [
    { emoji: '⚕️', title: '"Best doctor near me"', description: 'The #1 healthcare AI query. Are you the answer?' },
    { emoji: '🏥', title: '"Specialist in [condition]"', description: 'AI matches patients with specialists by expertise.' },
    { emoji: '📋', title: '"Doctor accepting patients"', description: 'New patient acquisition now starts with AI.' }
  ],
  benefits: [
    { title: 'Specialty Visibility', description: 'Your expertise found by AI' },
    { title: 'Credential Highlighting', description: 'Board certifications & experience shown' },
    { title: 'Patient Matching', description: 'Get recommended for your ideal patients' },
    { title: 'Trust Signals', description: 'Reviews & ratings boost AI recommendations' }
  ],
  solutionBg: 'bg-gradient-to-r from-blue-900 to-slate-900',
  solutionBadge: 'bg-blue-600 text-white',
  socialProof: '600+ medical practices'
});

const HomeServicesTemplate = createIndustryTemplate({
  gradient: 'from-slate-900 via-blue-900 to-slate-900',
  accentBg: 'bg-blue-600',
  icon: Building2,
  badge: 'For Home Service Businesses',
  headline: 'Is AI Recommending Your Business?',
  subheadline: 'When homeowners need urgent help and ask "plumber near me" or "emergency HVAC" — are YOU the AI recommendation?',
  urgency: 'Emergency home service searches convert at 5x the rate. Don\'t lose them to AI.',
  buttonClass: 'bg-blue-600 hover:bg-blue-700',
  businessType: 'Business',
  searchQuery: 'Homeowners Need Help',
  stats: [
    { value: '71%', label: 'Emergency searches use AI' },
    { value: '$450', label: 'Avg. service ticket' },
    { value: '5.1x', label: 'Higher conversion rate' }
  ],
  painPointHeadline: 'Emergency Calls Are Going to AI-Visible Competitors',
  painPointSubheadline: 'When pipes burst at 2am, homeowners ask AI. Who are they recommending?',
  painPoints: [
    { emoji: '🔧', title: '"Emergency plumber NOW"', description: 'High-urgency, high-value calls going to competitors.' },
    { emoji: '❄️', title: '"AC repair near me"', description: 'HVAC emergencies = premium pricing opportunities.' },
    { emoji: '⚡', title: '"24/7 electrician"', description: 'Round-the-clock service seekers using AI.' }
  ],
  benefits: [
    { title: 'Emergency Visibility', description: 'Be first for urgent service calls' },
    { title: 'Service Area Coverage', description: 'Get found across your entire territory' },
    { title: '24/7 Presence', description: 'AI recommends you even when you\'re sleeping' },
    { title: 'Trust & Licensing', description: 'Credentials featured in AI responses' }
  ],
  solutionBg: 'bg-gradient-to-r from-blue-900 to-slate-900',
  solutionBadge: 'bg-blue-600 text-white',
  socialProof: '2,000+ home service pros'
});

const LegalTemplate = createIndustryTemplate({
  gradient: 'from-slate-800 via-slate-900 to-black',
  accentBg: 'bg-slate-600',
  icon: Building2,
  badge: 'For Law Firms',
  headline: 'Is AI Recommending Your Firm?',
  subheadline: 'When people need legal help and ask "best lawyer for [case type]" — is YOUR firm being recommended by AI?',
  urgency: 'Legal clients increasingly use AI for attorney research before calling.',
  buttonClass: 'bg-slate-700 hover:bg-slate-800',
  businessType: 'Firm',
  searchQuery: 'Clients Need Legal Help',
  stats: [
    { value: '54%', label: 'Legal searches use AI' },
    { value: '$3,500', label: 'Avg. case value' },
    { value: '2.8x', label: 'More qualified consultations' }
  ],
  painPointHeadline: 'Potential Clients Are Asking AI for Attorney Recommendations',
  painPointSubheadline: 'Legal service discovery is changing. AI is the new referral network.',
  painPoints: [
    { emoji: '⚖️', title: '"Best lawyer for [case]"', description: 'Practice-area specific queries going to competitors.' },
    { emoji: '💼', title: '"Business attorney near me"', description: 'Corporate clients using AI for firm selection.' },
    { emoji: '🏛️', title: '"Experienced trial lawyer"', description: 'High-stakes clients trust AI recommendations.' }
  ],
  benefits: [
    { title: 'Practice Area Visibility', description: 'Get found for your specialties' },
    { title: 'Credential Highlighting', description: 'Bar admissions & experience featured' },
    { title: 'Case Match', description: 'AI connects you with ideal clients' },
    { title: 'Authority Building', description: 'Establish thought leadership in AI' }
  ],
  solutionBg: 'bg-gradient-to-r from-slate-800 to-slate-900',
  solutionBadge: 'bg-slate-600 text-white',
  socialProof: '400+ law firms'
});

const RealEstateTemplate = createIndustryTemplate({
  gradient: 'from-slate-900 via-blue-900 to-slate-900',
  accentBg: 'bg-blue-600',
  icon: Building2,
  badge: 'For Real Estate Professionals',
  headline: 'Is AI Recommending You to Buyers & Sellers?',
  subheadline: 'When people ask "best realtor in [city]" or "top real estate agent near me" — does AI mention YOUR name?',
  urgency: 'Home buyers and sellers are using AI to find their agent. Be that agent.',
  buttonClass: 'bg-blue-600 hover:bg-blue-700',
  businessType: 'Services',
  searchQuery: 'Buyers & Sellers Need an Agent',
  stats: [
    { value: '63%', label: 'Home searches start with AI' },
    { value: '$8,400', label: 'Avg. commission' },
    { value: '3.5x', label: 'More listing appointments' }
  ],
  painPointHeadline: 'Buyers & Sellers Are Finding Agents Through AI',
  painPointSubheadline: 'The real estate discovery process has changed. AI is the new referral.',
  painPoints: [
    { emoji: '🏡', title: '"Best realtor for first-time buyers"', description: 'Specific buyer segments asking AI for agents.' },
    { emoji: '📈', title: '"Top listing agent in [area]"', description: 'Sellers using AI to find proven performers.' },
    { emoji: '🔑', title: '"Real estate agent specializing in..."', description: 'Niche expertise queries going to AI-visible agents.' }
  ],
  benefits: [
    { title: 'Market Expertise Visibility', description: 'Your local knowledge featured by AI' },
    { title: 'Buyer/Seller Matching', description: 'Get recommended to your ideal clients' },
    { title: 'Transaction History', description: 'Your success record boosts AI trust' },
    { title: 'Neighborhood Authority', description: 'Own your farm area in AI search' }
  ],
  solutionBg: 'bg-gradient-to-r from-blue-900 to-slate-900',
  solutionBadge: 'bg-blue-600 text-white',
  socialProof: '1,500+ real estate pros'
});

const FitnessTemplate = createIndustryTemplate({
  gradient: 'from-slate-900 via-blue-900 to-slate-900',
  accentBg: 'bg-blue-600',
  icon: Building2,
  badge: 'For Fitness Businesses',
  headline: 'Is AI Recommending Your Gym?',
  subheadline: 'When people ask "best gym near me" or "personal trainer in [city]" — is YOUR fitness business the AI answer?',
  urgency: 'New Year\'s resolutions start with AI search. Be ready.',
  buttonClass: 'bg-blue-600 hover:bg-blue-700',
  businessType: 'Gym',
  searchQuery: 'People Search for Fitness',
  stats: [
    { value: '67%', label: 'Gym searches use AI' },
    { value: '$1,200', label: 'Avg. annual membership' },
    { value: '4.2x', label: 'More trial sign-ups' }
  ],
  painPointHeadline: 'Fitness Seekers Are Asking AI for Gym Recommendations',
  painPointSubheadline: 'The fitness industry is going AI-first. Is your gym visible?',
  painPoints: [
    { emoji: '💪', title: '"Best gym for beginners"', description: 'New fitness enthusiasts using AI to find welcoming gyms.' },
    { emoji: '🏋️', title: '"Personal trainer near me"', description: 'High-value PT leads going to AI-visible trainers.' },
    { emoji: '🧘', title: '"Yoga studio in [area]"', description: 'Specialty fitness searches flowing through AI.' }
  ],
  benefits: [
    { title: 'Class Visibility', description: 'Your programs found by AI' },
    { title: 'Trainer Matching', description: 'Connect with clients seeking your specialty' },
    { title: 'Facility Features', description: 'Equipment & amenities highlighted' },
    { title: 'Community Building', description: 'Attract members aligned with your culture' }
  ],
  solutionBg: 'bg-gradient-to-r from-blue-900 to-slate-900',
  solutionBadge: 'bg-blue-600 text-white',
  socialProof: '900+ fitness businesses'
});

const SalonSpaTemplate = createIndustryTemplate({
  gradient: 'from-slate-900 via-blue-900 to-slate-900',
  accentBg: 'bg-blue-600',
  icon: Building2,
  badge: 'For Salons & Spas',
  headline: 'Is AI Recommending Your Salon?',
  subheadline: 'When clients search "best hair salon near me" or "spa with good reviews" — is YOUR business being mentioned by AI?',
  urgency: 'Beauty clients trust AI recommendations. Make sure they find you.',
  buttonClass: 'bg-blue-600 hover:bg-blue-700',
  businessType: 'Salon',
  searchQuery: 'Clients Search for Beauty Services',
  stats: [
    { value: '72%', label: 'Beauty searches use AI' },
    { value: '$180', label: 'Avg. service ticket' },
    { value: '3.9x', label: 'More booking requests' }
  ],
  painPointHeadline: 'Clients Are Finding Their Next Salon Through AI',
  painPointSubheadline: 'The beauty industry is being transformed by AI discovery.',
  painPoints: [
    { emoji: '💅', title: '"Best nail salon near me"', description: 'Regular appointment seekers using AI for discovery.' },
    { emoji: '💇', title: '"Hair stylist specializing in..."', description: 'Specific style requests going to AI-visible stylists.' },
    { emoji: '🧖', title: '"Spa day packages"', description: 'High-value spa clients asking AI for recommendations.' }
  ],
  benefits: [
    { title: 'Service Visibility', description: 'All your treatments found by AI' },
    { title: 'Style Matching', description: 'Get recommended for your specialties' },
    { title: 'Review Integration', description: 'Your 5-star reviews boost AI rankings' },
    { title: 'Appointment Ready', description: 'AI sends ready-to-book clients' }
  ],
  solutionBg: 'bg-gradient-to-r from-blue-900 to-slate-900',
  solutionBadge: 'bg-blue-600 text-white',
  socialProof: '1,100+ salons & spas'
});

const PetServicesTemplate = createIndustryTemplate({
  gradient: 'from-slate-900 via-blue-900 to-slate-900',
  accentBg: 'bg-blue-600',
  icon: Building2,
  badge: 'For Pet Service Businesses',
  headline: 'Is AI Recommending Your Business?',
  subheadline: 'When pet parents ask "best vet near me" or "dog groomer in [city]" — is YOUR business the AI recommendation?',
  urgency: 'Pet parents trust AI for their fur babies. Be their top choice.',
  buttonClass: 'bg-blue-600 hover:bg-blue-700',
  businessType: 'Business',
  searchQuery: 'Pet Parents Search for Services',
  stats: [
    { value: '69%', label: 'Pet searches use AI' },
    { value: '$650', label: 'Avg. annual spend per pet' },
    { value: '4.1x', label: 'More appointment bookings' }
  ],
  painPointHeadline: 'Pet Parents Are Asking AI for Recommendations',
  painPointSubheadline: 'Pet care discovery has gone AI. Is your business visible to pet parents?',
  painPoints: [
    { emoji: '🐕', title: '"Best vet for dogs near me"', description: 'Pet parents trust AI for healthcare recommendations.' },
    { emoji: '🐩', title: '"Dog groomer with good reviews"', description: 'Grooming clients searching via AI assistants.' },
    { emoji: '🐾', title: '"Pet boarding while on vacation"', description: 'High-value boarding clients using AI search.' }
  ],
  benefits: [
    { title: 'Service Visibility', description: 'All your pet services found by AI' },
    { title: 'Pet Type Matching', description: 'Get recommended for specific animals' },
    { title: 'Trust Building', description: 'Your credentials highlighted in AI' },
    { title: 'Emergency Presence', description: 'Be found for urgent pet care needs' }
  ],
  solutionBg: 'bg-gradient-to-r from-blue-900 to-slate-900',
  solutionBadge: 'bg-blue-600 text-white',
  socialProof: '750+ pet service businesses'
});

// Template mapping
const TEMPLATES = {
  'default': DefaultTemplate,
  'sales-funnel': SalesFunnelTemplate,
  'case-study': CaseStudyTemplate,
  'comparison': ComparisonTemplate,
  'urgency': UrgencyTemplate,
  // Industry-specific templates
  'auto-dealership': AutoDealershipTemplate,
  'restaurant': RestaurantTemplate,
  'dental': DentalTemplate,
  'medical': MedicalTemplate,
  'home-services': HomeServicesTemplate,
  'legal': LegalTemplate,
  'real-estate': RealEstateTemplate,
  'fitness': FitnessTemplate,
  'salon-spa': SalonSpaTemplate,
  'pet-services': PetServicesTemplate
};

const TEMPLATE_INFO = {
  'default': { name: 'AI Visibility', description: 'Focus on AI recommendations' },
  'sales-funnel': { name: 'Sales Funnel', description: 'Problem-agitation-solution' },
  'case-study': { name: 'Case Study', description: 'Results-focused' },
  'comparison': { name: 'Before/After', description: 'Transformation showcase' },
  'urgency': { name: 'Urgency', description: 'Limited time style' },
  // Industry-specific
  'auto-dealership': { name: 'Auto Dealership', description: 'Car dealers & automotive' },
  'restaurant': { name: 'Restaurant', description: 'Food service & dining' },
  'dental': { name: 'Dental Practice', description: 'Dentists & oral health' },
  'medical': { name: 'Medical/Healthcare', description: 'Doctors & clinics' },
  'home-services': { name: 'Home Services', description: 'Plumbers, electricians, HVAC' },
  'legal': { name: 'Law Firm', description: 'Attorneys & legal services' },
  'real-estate': { name: 'Real Estate', description: 'Realtors & property' },
  'fitness': { name: 'Fitness & Gym', description: 'Gyms & trainers' },
  'salon-spa': { name: 'Salon & Spa', description: 'Beauty services' },
  'pet-services': { name: 'Pet Services', description: 'Vets & pet care' }
};

// Custom Template Renderer
const CustomTemplateRenderer = ({ templateData, affiliate, affiliateCode, analyzerUrl }) => {
  const hero = templateData.hero || {};
  const stats = templateData.stats || [];
  const benefits = templateData.benefits || [];
  const testimonial = templateData.testimonial || {};
  const cta = templateData.cta || {};
  const style = templateData.style || {};
  
  // Professional blue/grey/white color scheme
  const primaryColor = style.primaryColor || '#2563eb'; // Blue-600
  const gradientFrom = style.gradientFrom || '#0f172a'; // Slate-900
  const gradientTo = style.gradientTo || '#1e3a8a'; // Blue-900
  const theme = style.theme || 'dark';

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>
      {/* Hero Section */}
      <section 
        className="relative py-20 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
      >
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <Badge className="bg-white/20 text-white mb-6 backdrop-blur-sm">
            <Sparkles className="h-4 w-4 mr-2 inline" />
            Recommended by {affiliate?.name || 'Our Partner'}
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            {hero.headline || 'Is AI Recommending Your Business?'}
          </h1>
          
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            {hero.subheadline || '60% of consumers now ask AI for business recommendations.'}
          </p>

          <a href={analyzerUrl}>
            <Button 
              size="lg" 
              className="bg-white text-slate-900 hover:bg-slate-100 font-bold text-lg px-8 py-6 h-auto shadow-2xl"
            >
              {hero.ctaText || 'Check Your AI Visibility FREE'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
          
          {hero.ctaSubtext && (
            <p className="text-white/60 text-sm mt-4">{hero.ctaSubtext}</p>
          )}
        </div>
      </section>

      {/* Stats Section */}
      {stats.length > 0 && (
        <section className={`py-12 ${theme === 'dark' ? 'bg-slate-900' : 'bg-slate-50'}`}>
          <div className="max-w-4xl mx-auto px-4">
            <div className="grid grid-cols-3 gap-4">
              {stats.map((stat, idx) => (
                <div 
                  key={idx} 
                  className={`text-center p-6 rounded-xl ${theme === 'dark' ? 'bg-slate-800' : 'bg-white shadow'}`}
                >
                  <div 
                    className="text-3xl font-bold mb-2"
                    style={{ color: primaryColor }}
                  >
                    {stat.value}
                  </div>
                  <div className={theme === 'dark' ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {benefits.length > 0 && (
        <section className={`py-16 ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4">
            <h2 className={`text-2xl font-bold text-center mb-10 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
              What You'll Discover
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, idx) => (
                <div 
                  key={idx}
                  className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-slate-900 border border-slate-800' : 'bg-slate-50'}`}
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${primaryColor}20` }}
                  >
                    <CheckCircle2 className="h-6 w-6" style={{ color: primaryColor }} />
                  </div>
                  <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                    {benefit.title}
                  </h3>
                  <p className={theme === 'dark' ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial Section */}
      {testimonial.quote && (
        <section 
          className="py-16"
          style={{ background: `linear-gradient(135deg, ${gradientFrom}20, ${gradientTo}20)` }}
        >
          <div className="max-w-3xl mx-auto px-4 text-center">
            <Quote className="h-10 w-10 mx-auto mb-6" style={{ color: primaryColor }} />
            <p className={`text-xl italic mb-6 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
              "{testimonial.quote}"
            </p>
            <div>
              <p className="font-semibold" style={{ color: primaryColor }}>{testimonial.author}</p>
              <p className={theme === 'dark' ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                {testimonial.role}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section 
        className="py-20"
        style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
      >
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {cta.headline || "Don't Let AI Recommend Your Competitors"}
          </h2>
          <p className="text-white/80 mb-8">
            {cta.subheadline || 'Find out in 30 seconds if your business is visible to AI'}
          </p>
          
          <a href={analyzerUrl}>
            <Button 
              size="lg" 
              className="bg-white text-slate-900 hover:bg-slate-100 font-bold text-lg px-10 py-6 h-auto"
            >
              {cta.buttonText || 'Get My Free Analysis'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
          
          <div className="mt-8 flex items-center justify-center gap-4 text-white/70 text-sm">
            <div className="flex items-center gap-1">
              <Shield className="h-4 w-4" />
              <span>100% Free</span>
            </div>
            <div className="flex items-center gap-1">
              <Zap className="h-4 w-4" />
              <span>Instant Results</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-8 border-t ${theme === 'dark' ? 'border-slate-800 bg-slate-950' : 'border-slate-200 bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className={theme === 'dark' ? 'text-slate-500 text-sm' : 'text-slate-500 text-sm'}>
            © 2026 UPost • Partner: {affiliateCode}
          </p>
        </div>
      </footer>
    </div>
  );
};

function AffiliatePromoPage() {
  const params = useParams();
  const affiliateCode = params.affiliateCode || localStorage.getItem('affiliate_code');
  const templateParam = params.template;
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tracked, setTracked] = useState(false);
  const [customTemplateData, setCustomTemplateData] = useState(null);
  
  // Check if this is a custom template
  const isCustomTemplate = templateParam && templateParam.startsWith('custom-');
  const customTemplateId = isCustomTemplate ? templateParam.replace('custom-', '') : null;
  
  // Determine which template to use for built-in templates
  const template = !isCustomTemplate && templateParam && TEMPLATES[templateParam] ? templateParam : 'default';
  const TemplateComponent = TEMPLATES[template];

  useEffect(() => {
    if (affiliateCode) {
      fetchAffiliateInfo();
      if (isCustomTemplate && customTemplateId) {
        fetchCustomTemplate();
      }
      trackClick();
    }
  }, [affiliateCode, templateParam]);

  const fetchAffiliateInfo = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/public/${affiliateCode}`);
      setAffiliate(response.data);
    } catch (error) {
      console.log('Affiliate not found, using default');
      setAffiliate({ name: 'Partner', code: affiliateCode });
    } finally {
      if (!isCustomTemplate) {
        setLoading(false);
      }
    }
  };

  const fetchCustomTemplate = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/custom-template/${affiliateCode}/${customTemplateId}`);
      if (response.data.success) {
        setCustomTemplateData(response.data.template);
      }
    } catch (error) {
      console.log('Custom template not found, using default');
    } finally {
      setLoading(false);
    }
  };

  const trackClick = async () => {
    if (tracked) return;
    try {
      await axios.post(`${API}/affiliate/track-template-click`, { 
        affiliate_code: affiliateCode,
        template: isCustomTemplate ? `custom-${customTemplateId}` : template,
        source: 'promo_page',
        referrer: document.referrer || null,
        user_agent: navigator.userAgent
      });
      
      // Track OG image impression for A/B testing
      await axios.post(`${API}/og/track/impression`, {
        affiliate_code: affiliateCode,
        template: isCustomTemplate ? `custom-${customTemplateId}` : template,
        page_type: 'promo',
        source: new URLSearchParams(window.location.search).get('utm_source') || 'direct',
        referrer: document.referrer || null
      });
      
      setTracked(true);
    } catch (error) {
      console.log('Click tracking failed');
    }
  };

  // Use relative URL for analyzer - points to the REAL AI visibility checker with affiliate tracking
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://upost.com';
  const analyzerUrl = `${baseUrl}/ai-check?ref=${affiliateCode}`;
  const ogImageUrl = `${API_URL}/api/og/affiliate/${affiliateCode}?template=${isCustomTemplate ? 'default' : template}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Render custom template if available
  if (isCustomTemplate && customTemplateData) {
    return (
      <>
        <CustomTemplateRenderer 
          templateData={customTemplateData}
          affiliate={affiliate}
          affiliateCode={affiliateCode}
          analyzerUrl={analyzerUrl}
        />
      </>
    );
  }

  return (
    <>

      <TemplateComponent 
        affiliate={affiliate}
        affiliateCode={affiliateCode}
        analyzerUrl={analyzerUrl}
        ogImageUrl={ogImageUrl}
      />
    </>
  );
}

export default AffiliatePromoPage;
